/**
 * Normalize social media URLs to ensure they are valid and functional
 * Based on demo2 implementation patterns
 */

/**
 * Build a proper social media URL for different platforms
 * @param {string} platform - The social media platform
 * @param {string} value - The username or URL provided by user
 * @returns {string} - The normalized URL
 */
export const buildSocialUrl = (platform, value) => {
  if (!value || typeof value !== 'string') return '';
  
  const cleanValue = value.trim();
  if (!cleanValue) return '';

  // If it's already a full URL, return it as-is (with some validation)
  if (cleanValue.startsWith('http://') || cleanValue.startsWith('https://')) {
    return cleanValue;
  }

  switch (platform.toLowerCase()) {
    case 'whatsapp':
      // Clean phone number: remove spaces, +, -, (), etc.
      const phoneNumber = cleanValue.replace(/[\s\+\-\(\)]/g, '');
      return `https://wa.me/${phoneNumber}`;
      
    case 'facebook':
      // Handle facebook.com/username format or just username
      const fbUser = cleanValue.replace('facebook.com/', '').replace('fb.com/', '');
      return `https://facebook.com/${fbUser}`;
      
    case 'instagram':
      // Handle @username or just username
      const igUser = cleanValue.startsWith('@') ? cleanValue.slice(1) : cleanValue;
      return `https://instagram.com/${igUser}`;
      
    case 'linkedin':
      // Handle linkedin.com/in/username or just username
      const liUser = cleanValue.replace('linkedin.com/in/', '').replace('linkedin.com/', '');
      return `https://linkedin.com/in/${liUser}`;
      
    case 'tiktok':
      // Handle @username or just username
      const ttUser = cleanValue.startsWith('@') ? cleanValue.slice(1) : cleanValue;
      return `https://www.tiktok.com/@${ttUser}`;
      
    case 'youtube':
      // Handle @username, youtube.com/@username or just username
      let ytUser = cleanValue;
      if (cleanValue.startsWith('@')) {
        ytUser = cleanValue;
      } else if (cleanValue.includes('youtube.com/@')) {
        ytUser = '@' + cleanValue.split('@')[1];
      } else {
        ytUser = '@' + cleanValue;
      }
      return `https://youtube.com/${ytUser}`;
      
    case 'twitter':
      // Handle @username or just username
      const twUser = cleanValue.startsWith('@') ? cleanValue.slice(1) : cleanValue;
      return `https://twitter.com/${twUser}`;
      
    case 'telegram':
      // Handle @username or just username
      const tgUser = cleanValue.startsWith('@') ? cleanValue.slice(1) : cleanValue;
      return `https://t.me/${tgUser}`;
      
    default:
      // For unknown platforms, try to build a reasonable URL
      return `https://${platform.toLowerCase()}.com/${cleanValue}`;
  }
};

/**
 * Normalize all social links in an object
 * @param {Object} socialLinks - Object with platform names as keys and usernames/URLs as values
 * @returns {Object} - Object with normalized URLs
 */
export const normalizeSocialLinks = (socialLinks) => {
  if (!socialLinks || typeof socialLinks !== 'object') return {};
  
  const normalized = {};
  
  Object.entries(socialLinks).forEach(([platform, value]) => {
    if (value && typeof value === 'string' && value.trim()) {
      normalized[platform] = buildSocialUrl(platform, value);
    }
  });
  
  return normalized;
};

/**
 * Get the brand color for a social media platform
 * @param {string} platform - The social media platform
 * @returns {string} - The brand color in hex format
 */
export const getSocialBrandColor = (platform) => {
  const colors = {
    whatsapp: '#25D366',
    facebook: '#1877F2',
    instagram: '#E4405F',
    linkedin: '#0A66C2',
    tiktok: '#000000',
    youtube: '#FF0000',
    twitter: '#1DA1F2',
    telegram: '#0088CC',
    default: '#6B7280'
  };
  
  return colors[platform.toLowerCase()] || colors.default;
};